import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { COUNTRIES, ENVIRONMENTS, type CountryCode, type Environment } from './types'

// Shared environment + country context. Every module reads these defaults; the
// header selector sets them. DEV/STAGING only — no production option exists.
interface EnvState {
  environment: Environment
  country: CountryCode
  setEnvironment: (e: Environment) => void
  setCountry: (c: CountryCode) => void
}

const EnvCtx = createContext<EnvState | null>(null)
const LS_KEY = 'assetsales-toolbox.env'

export function EnvProvider({ children }: { children: ReactNode }) {
  const [environment, setEnvironment] = useState<Environment>('DEVELOP')
  const [country, setCountry] = useState<CountryCode>('KE')

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(LS_KEY) || '{}')
      if (saved.environment) setEnvironment(saved.environment)
      if (saved.country) setCountry(saved.country)
    } catch { /* ignore */ }
  }, [])

  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify({ environment, country }))
  }, [environment, country])

  const value = useMemo(
    () => ({ environment, country, setEnvironment, setCountry }),
    [environment, country],
  )
  return <EnvCtx.Provider value={value}>{children}</EnvCtx.Provider>
}

export function useEnv(): EnvState {
  const v = useContext(EnvCtx)
  if (!v) throw new Error('useEnv must be used inside <EnvProvider>')
  return v
}

export function EnvSelector() {
  const { environment, country, setEnvironment, setCountry } = useEnv()
  return (
    <div className="env-selector">
      <div className="seg" role="group" aria-label="Environment">
        {ENVIRONMENTS.map((o) => (
          <button
            key={o.value}
            type="button"
            className={`seg-btn ${environment === o.value ? 'on' : ''}`}
            aria-pressed={environment === o.value}
            onClick={() => setEnvironment(o.value)}
          >
            {o.label}
          </button>
        ))}
      </div>
      <label className="field">
        <span className="field-label">Country</span>
        <select value={country} onChange={(e) => setCountry(e.target.value as CountryCode)}>
          {COUNTRIES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </label>
    </div>
  )
}
